import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { useAuth } from "@/features/authentication";
import { CenteredLoading } from "@/features/ui";

import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./expense-accrual-form-constants";
import { ExpenseAccrualDetailsSection } from "./expense-accrual-details-section";
import { GeneralInformationSection } from "./general-information-section";

export const ExpenseAccrualForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const { outlet } = useAuth();

  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState } = methods;

  const onFormSubmit = async (formData) => {
    const payload = {
      // The page lifts this out and sends it as the Outlet-Id header; the
      // document is always recorded against the tab's active outlet.
      outletId: outlet?.id,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      dueDate: dayjs(formData.dueDate).format("YYYY-MM-DD"),
      expensePayableOutletChartOfAccountId:
        formData.expensePayableOutletChartOfAccount.id,
      note: formData.note || null,
      // Full replace: every line is sent every time, so one left out is deleted.
      expenseAccrualDetails: formData.expenseAccrualDetails.map((line) => ({
        outletChartOfAccountId: line.outletChartOfAccount.id,
        amount: Number(line.amount),
        note: line.note || null,
      })),
    };

    await onSubmit(payload);
  };

  if (isLoading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ExpenseAccrualDetailsSection />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

ExpenseAccrualForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
